import React from "react";
import {CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import useAnalysisData from "./useAnalysisData.js";
import {formatEuro, formatEuroCompact, monochromeScale} from "../Statistics/format.js";
import AnalysisCard from "./AnalysisCard.jsx";

const AKTUELLES_JAHR_FARBE = "#2f6fd0";

// Grauton-Basis der Vorjahre, NICHT die Hauptfarbe - so bleibt das laufende
// Jahr optisch eindeutig das einzige "kräftige, farbige" Jahr. lightest ist
// bewusst auf 0,35 begrenzt (statt des Standardwerts 0,82): selbst das
// älteste Vorjahr bleibt damit auf Weiß klar erkennbar (Kontrast ~3,6:1) -
// eine hellere Abstufung wäre laut Sichtprüfung "nahezu unsichtbar"
// gewesen. Zusätzlich zur Abstufung bekommt jedes Vorjahr ein eigenes
// Strichmuster, damit die Unterscheidung nie an der Farbe allein hängt.
const VORJAHR_BASIS_FARBE = "#343a40";
const VORJAHR_HELLSTER_TON = 0.35;
const VORJAHR_STRICHMUSTER = ["", "6 3", "2 2", "8 3 2 3", "1 3"];

const MONAT_KURZ = [
    "Jan", "Feb", "Mär", "Apr", "Mai", "Jun",
    "Jul", "Aug", "Sep", "Okt", "Nov", "Dez",
];

function monatKurz(month) {
    return MONAT_KURZ[Number(month) - 1] || String(month);
}

/**
 * Linienstil je Jahr: laufendes Jahr kräftig in der Hauptfarbe, Vorjahre in
 * abgestuften Grautönen (unmittelbares Vorjahr am dunkelsten/kräftigsten,
 * ältere Jahre zunehmend heller) plus zyklisch wechselndem Strichmuster.
 */
function jahresStile(years, currentYear) {
    const currentYearStr = currentYear ? String(currentYear) : null;
    const vorjahreAbsteigend = years.filter((year) => year !== currentYearStr).sort((a, b) => Number(b) - Number(a));
    const grauskala = monochromeScale(VORJAHR_BASIS_FARBE, vorjahreAbsteigend.length || 1, {lightest: VORJAHR_HELLSTER_TON, darkest: 0});

    const stile = {};
    vorjahreAbsteigend.forEach((year, index) => {
        stile[year] = {
            farbe: grauskala[index],
            strichmuster: VORJAHR_STRICHMUSTER[index % VORJAHR_STRICHMUSTER.length],
            breite: index === 0 ? 2.2 : 1.6,
        };
    });
    if (currentYearStr) {
        stile[currentYearStr] = {farbe: AKTUELLES_JAHR_FARBE, strichmuster: "", breite: 3};
    }
    return stile;
}

/**
 * Kumulierte Umsätze je Monat, eine Linie pro Jahr im gewählten Zeitraum
 * (Excel-Blatt "Kumuliert" - dort statische, veraltete Werte, hier live aus
 * GET /vertrieb/daten/kumuliert mit denselben Filtern wie die Sicht Gesamt).
 * Das laufende Jahr endet am Stichmonat statt auf 0 abzufallen (siehe
 * SalesAnalysisQuery::kumuliert()). Es ist die einzige farbige, kräftige
 * Linie; die Vorjahre laufen in abgestuften Grautönen plus eigenem
 * Strichmuster je Jahr (siehe jahresStile()) - Sichtprüfung 14.09.2026
 * ergab, dass reine Farbabstufungen benachbarter Vorjahre kaum
 * unterscheidbar und die hellste Legendenfarbe fast unsichtbar waren.
 */
export default function KumuliertChart({filters, className = "mb-3"}) {
    const {meta, params} = filters;
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/kumuliert", params);
    const rows = data?.rows || [];

    const years = Object.keys(rows[0]?.years || {}).sort((a, b) => Number(a) - Number(b));
    const chartData = rows.map((row) => ({month: row.month, ...row.years}));
    const hatUmsatz = chartData.some((row) => years.some((year) => Number(row[year]) > 0));

    const stile = jahresStile(years, meta?.current_year);

    return (
        <AnalysisCard
            title="Kumulierte Umsätze (nur Rechnungswerte)"
            help="Laufende Summe des Nettoumsatzes je Monat, eine Linie pro Jahr im gewählten Zeitraum. Das laufende Jahr endet am Stichmonat."
            loading={loading}
            error={error}
            empty={!loading && (rows.length === 0 || !hatUmsatz)}
            height={280}
            className={className}
        >
            <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tickFormatter={monatKurz} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatEuroCompact} tick={{fontSize: 12}} width={80} axisLine={false} tickLine={false} />
                    <Tooltip formatter={(value) => formatEuro(value)} labelFormatter={(month) => monatKurz(month)} />
                    <Legend />
                    {years.map((year) => {
                        const stil = stile[year] || {farbe: VORJAHR_BASIS_FARBE, strichmuster: "", breite: 1.6};
                        return (
                            <Line
                                key={year}
                                type="monotone"
                                dataKey={year}
                                name={Number(year) === Number(meta?.current_year) ? `${year} (laufend)` : year}
                                stroke={stil.farbe}
                                strokeWidth={stil.breite}
                                strokeDasharray={stil.strichmuster || undefined}
                                dot={false}
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>
        </AnalysisCard>
    );
}
